import type { I18nKey } from '@pos/i18n';
import { localized } from '@pos/i18n';

/** Field kinds the generated resource definitions use (tools/codegen). */
export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'decimal'
  | 'toggle'
  | 'select'
  | 'reference'
  | 'date'
  | 'datetime'
  | 'time'
  | 'json'
  | 'localized'
  | 'weekdays';

export interface FieldOption {
  value: string | number;
  label: I18nKey;
}

export interface FieldDef {
  key: string;
  label: I18nKey;
  type: FieldType;
  required?: boolean;
  options?: readonly FieldOption[];
  /** Resource key a reference points at, e.g. `catalog.item`. */
  ref?: string;
  /** Helper text under the input. */
  helper?: I18nKey;
  /** Not shown in the form (the value is still sent as loaded). */
  hidden?: boolean;
  /**
   * A reference that offers only some of its rows, which depend on another field of the form: the ids listed at `path` (its
   * `{id}` is that field's value) under `valueField`, active ones only when `activeField` is set. For example an option's unit:
   * only the units of the chosen item (ADR 0030).
   */
  limitTo?: { field: string; fieldLabel: I18nKey; path: string; valueField: string; activeField?: string };
}

export interface ColumnDef {
  key: string;
  header: I18nKey;
  type: FieldType;
  options?: readonly FieldOption[];
  ref?: string;
}

/** A list filter (tools/codegen): its query parameter, label and kind; dates filter by a from–to range. */
export interface FilterDef {
  key: string;
  label: I18nKey;
  type: FieldType;
  options?: readonly FieldOption[];
  ref?: string;
}

/** One generated master-data resource: where its API is, what to list, what to edit. */
export interface ResourceDef {
  key: string;
  title: I18nKey;
  /** API path; children contain `{parentId}`. */
  path: string;
  readPermission: string;
  writePermission: string | null;
  /** Fields that name a record, e.g. code and name. */
  labelFields: readonly string[];
  /** The key other records use to point here; `id` unless the table is keyed by code. */
  valueField?: string;
  columns: readonly ColumnDef[];
  fields: readonly FieldDef[];
  empty: Readonly<Record<string, unknown>>;
  children: readonly string[];
  /** Screen route of a top-level resource, e.g. `/catalog/items`. */
  route?: string;
  group?: string;
  parent?: string;
  /** Every row of a child resource, for pickers elsewhere. */
  lookupPath?: string;
  /**
   * A top-level resource also listed as a tab on the records it can point to, e.g. a card on its contact: the tab lists
   * the rows whose `field` is that record, and creates new ones with it set.
   */
  tabOf?: readonly { parent: string; field: string }[];
  /** What the list can be filtered by, shown in the filter panel. */
  filters?: readonly FilterDef[];
  /** While the record's `when` field is on, these fields cannot be edited, e.g. a base unit's factor. */
  lock?: { when: string; fields: readonly string[] };
  /** Fields set when the record is created and never changed after, e.g. the brand of a row stores receive by brand (ADR 0132). */
  fixedOnEdit?: readonly string[];
  /** The fields that make up an address: the form shows one address picker for them (ADR 0026), when it has a location source. */
  address?: AddressFieldKeys;
}

/** The keys of the address fields of a resource (camelCase, as in the form values). */
export interface AddressFieldKeys {
  country: string;
  province: string;
  district: string;
  ward: string;
  postal: string;
  street: string;
  division: string;
}

export type ResourceMap = Readonly<Record<string, ResourceDef>>;

/** What an app changes on a generated definition without regenerating it: columns, per-field label/helper, lock. */
export interface ResourceOverride {
  columns?: readonly ColumnDef[];
  /** By field key; merged into the generated field (`label` is a table-specific label, `hidden` hides it). */
  fields?: Readonly<Record<string, Partial<FieldDef>>>;
  lock?: ResourceDef['lock'];
}

/** The generated resources with the app's overrides applied (labels per field name are shared between tables, e.g. `price`). */
export function applyOverrides(
  resources: ResourceMap,
  overrides: Readonly<Record<string, ResourceOverride>>,
): ResourceMap {
  return Object.fromEntries(
    Object.entries(resources).map(([key, def]) => {
      const o = overrides[key];
      if (!o) return [key, def];
      return [
        key,
        {
          ...def,
          columns: o.columns ?? def.columns,
          lock: o.lock ?? def.lock,
          fields: def.fields.map((f) => (o.fields?.[f.key] ? { ...f, ...o.fields[f.key] } : f)),
        },
      ];
    }),
  );
}

export type RecordValues = Record<string, unknown> & { id: string; version: number | string };

/** The API path for a list: children need their parent's id. */
export function resourcePath(def: ResourceDef, parentId?: string) {
  return parentId ? def.path.replace('{parentId}', parentId) : def.path;
}

/** Where pickers load every row of a resource from; null when rows only exist under a parent. */
export function lookupPath(def: ResourceDef) {
  return def.lookupPath ?? (def.path.includes('{parentId}') ? null : def.path);
}

/** A record's display name from its label fields: "PHO – Phở Thìn". */
export function recordLabel(def: ResourceDef, record: Record<string, unknown>) {
  const parts = def.labelFields.map((field) => {
    const value = record[camelCase(field)];
    if (value && typeof value === 'object') return localized(value as Record<string, string>, '');
    return value == null ? '' : String(value);
  });
  return parts.filter(Boolean).join(' – ') || String(record['id'] ?? '');
}

export function camelCase(name: string) {
  return name.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());
}

/** The values a form edits: the record's fields, starting from the definition's defaults. */
export function formValues(def: ResourceDef, record?: Record<string, unknown>) {
  const values: Record<string, unknown> = { ...def.empty };
  if (record) {
    for (const field of def.fields) values[field.key] = record[field.key] ?? def.empty[field.key] ?? null;
  }
  return values;
}

/** The days of a `weekdays` value, in bit order: Monday is 1, Sunday 64 (127 is every day). */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
